import { clsx } from 'clsx';
import { forwardRef } from 'react';
import { Link } from 'react-router';

import { getNodeText } from '../../internal/utils/getNodeText';
import { logger } from '../../internal/utils/logger';
import { marginCSSVars } from '../../utils/spacing';
import { LoadingLabel } from '../internal/loadingLabel/loadingLabel';
import { Loading } from '../loading/loading';
import { useReeferTheme } from '../providers/reeferThemeProvider/reeferThemeProvider';
import { Typography } from '../typography/typography';
import type { ButtonLabelProps, ButtonProps } from './button.types';
import { IconButton } from './iconButton/iconButton';
import { iconWithProps } from './iconWithProps';

import styles from './button.module.css';

function ButtonLabel({
  alignment = 'center',
  branded,
  endIcon,
  id,
  label,
  loading,
  size = 'default',
  startIcon,
  subLabel,
  typographyVariant,
}: ButtonLabelProps) {
  const isSmall = size === 'small';
  if (isSmall && subLabel) {
    logger.warn(
      'Button',
      'Inline button variants should not include a subLabel.'
    );
  }

  const stringifiedLabel = getNodeText(label);

  const accessibleStartIcon = iconWithProps(startIcon, stringifiedLabel);
  const accessibleEndIcon = iconWithProps(endIcon, stringifiedLabel);

  return (
    <LoadingLabel
      data-testid="loadingLabel"
      isLoading={loading}
      subLabel={subLabel}
    >
      <span
        className={clsx([styles[`button_iconLabel__${alignment}`]], {
          [styles['button_iconLabel']]: !!label,
        })}
      >
        {accessibleStartIcon}
        <span
          className={clsx({
            [styles['button_label']]: !!label,
            [styles['button_label__start']]: !!accessibleStartIcon,
            [styles['button_label__end']]: !!accessibleEndIcon,
          })}
        >
          {label && (
            <Typography
              id={id}
              variant={typographyVariant}
              branded={branded}
              textAlign={alignment}
              as="span"
              color="inherit"
            >
              {label}
            </Typography>
          )}
          {subLabel && (
            <Typography
              variant="small-body-regular"
              branded={branded}
              textAlign={alignment}
              as="span"
              mt={-4}
              color="inherit"
            >
              {subLabel}
            </Typography>
          )}
        </span>
        {accessibleEndIcon}
      </span>
    </LoadingLabel>
  );
}

const MainButton = forwardRef<HTMLButtonElement, ButtonProps>(
  function MainButton(
    {
      alignment = 'center',
      'aria-label': ariaLabel,
      ariaLabel: ariaLabel_toBeDeprecated,
      'aria-pressed': ariaPressed,
      autoFocus = false,
      branded = false,
      className: classNameProp,
      data,
      'data-testid': testId,
      disableRefDeprecationWarning = false,
      disabled = false,
      endIcon,
      full,
      form,
      size = 'default',
      href,
      id,
      label,
      loading = false,
      onClick,
      part,
      startIcon,
      style,
      subLabel,
      target,
      to,
      type = 'button',
      variant = 'primary',
      ...marginProps
    }: ButtonProps,
    ref
  ) {
    const theme = useReeferTheme();

    const className = clsx(
      classNameProp,
      styles['button'],
      styles['buttonText'],
      styles[`button__${size}`],
      styles[`button__${variant}`],
      {
        [styles['button__full']]: full,
        [styles['button__disabled']]: disabled,
        [styles['button__loading']]: loading,
        [styles[`button__${size}__withSubLabel`]]: !!subLabel,
      }
    );

    const fakeButton = !onClick && type === 'button';

    const buttonStyles = {
      ...theme.components.Button.shared,
      ...theme.components.Button.variants[variant],
      ...theme.components.Button.sizes[size],
    };

    const getDataProps = (data: ButtonProps['data']) => {
      const dataProps = {} as Record<string, string | number | boolean>;

      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          dataProps[`data-${key}`] = data[key];
        }
      }

      return dataProps;
    };

    const sharedProps = {
      'aria-label': ariaLabel || ariaLabel_toBeDeprecated,
      'aria-pressed': ariaPressed,
      'data-testid': testId,
      disabled,
      ...getDataProps(data),
    };

    const labelId = id ? `${id}-label` : undefined;

    const buttonLabel = (
      <ButtonLabel
        alignment={alignment}
        branded={branded}
        id={labelId}
        startIcon={startIcon}
        endIcon={endIcon}
        label={label}
        loading={loading}
        subLabel={subLabel}
        size={size}
        typographyVariant={buttonStyles.typographyVariant}
      />
    );

    if (ref) {
      if (to || href) {
        throw Error(
          '[Reefer/Button] refs are not recommended and cannot be used in conjunction with "to" or "href"'
        );
      }
      if (!disableRefDeprecationWarning) {
        logger.warn(
          'Button',
          'refs are deprecated and will eventually be removed'
        );
      }
    }

    const inlineStyles = {
      ...marginCSSVars({
        ...marginProps,
      }),
      ...style,
    };

    if (to) {
      return (
        <Link
          className={className}
          id={id}
          style={inlineStyles}
          onClick={onClick}
          to={to}
          target={target || '_self'}
          {...sharedProps}
          {...{ part }}
        >
          {loading && <Loading color={buttonStyles.typographyColor} />}
          {buttonLabel}
        </Link>
      );
    }

    if (href) {
      return (
        <a
          className={className}
          href={href}
          id={id}
          onClick={onClick}
          target={target || '_blank'}
          rel="noopener noreferrer"
          style={inlineStyles}
          {...sharedProps}
          {...{ part }}
        >
          {loading && <Loading color={buttonStyles.typographyColor} />}
          <span className={styles['button_newTabLabel']}>
            Opens in new window
          </span>{' '}
          {buttonLabel}
        </a>
      );
    }

    if (!href && !to && fakeButton) {
      return (
        <div
          className={className}
          id={id}
          style={inlineStyles}
          {...sharedProps}
          {...{ part }}
        >
          {loading && <Loading color={buttonStyles.typographyColor} />}
          {buttonLabel}
        </div>
      );
    }

    return (
      <button
        aria-labelledby={labelId}
        autoFocus={autoFocus}
        className={className}
        id={id}
        onClick={onClick}
        type={type}
        form={form}
        style={inlineStyles}
        {...sharedProps}
        {...{ part }}
      >
        {loading && <Loading color={buttonStyles.typographyColor} />}
        {buttonLabel}
      </button>
    );
  }
);

export const Button = Object.assign(MainButton, {
  Icon: IconButton,
});
